import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ActivatedRoute, UrlSegment } from '@angular/router';
import { IUser } from '../entities/user';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { environment } from '../../../environments/environment';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly storageKeyTokenUserId = 'token-user-id';

  /**
   *
   */
  constructor(private httpClient: HttpClient) {
    let current = this.stateLoad();
    if (!current) {
      current = { isLoggedIn: false };
      this.stateSave(current);
    }

    this.authState = new BehaviorSubject<IAuthState>(current);
  }

  tokenUserIdCache(tokenUserId: string) {
    localStorage.setItem(this.storageKeyTokenUserId, tokenUserId);
  }

  tokenUserId(): string {
    return localStorage.getItem(this.storageKeyTokenUserId);
  }

  tokenUserIdClear(): void {
    localStorage.removeItem(this.storageKeyTokenUserId);
  }

  currentState(): IAuthState {
    return this.authState.value;
  }

  canAccessRoute(url: UrlSegment[]): boolean {
    return this.currentState().isLoggedIn;
  }

  private authState: BehaviorSubject<IAuthState>;

  userName(): string {
    return this.currentState().isLoggedIn ? this.currentState().user.userName : undefined;
  }

  /**
   *
   */
  isAdministrator(): boolean {
    return this.userInRole(AuthServiceRoles.Administrator);
  }

  /**
   *
   * @returns
   */
  isCustomerRoleProductions(): boolean {
    return this.isCustomerRole('productions');
  }

  isCustomerRoleTours(): boolean {
    return this.isCustomerRole('tours');
  }

  isCustomerRoleEvents(): boolean {
    return this.isCustomerRole('events');
  }

  getUserRole(): string {
    return this.currentState().user?.customerRole;
  }

  getCustomerCodes(): string {
    return this.currentState().user?.customerCodes;
  }

  getParentGroupCodes(): string {
    return this.currentState().user?.parentGroupCodes;
  }

  /**
   *
   * @param customerRole
   * @returns
   */
  private isCustomerRole(customerRole: string): boolean {
    return this.currentState().user?.customerRole == customerRole;
  }

  /**
   *
   * @param roleCode
   */
  userInRole(roleCode: string | string[]): boolean {

    if (!this.currentState().user)
      return false;

    const codes = Array.isArray(roleCode) ? roleCode : [roleCode];

    const ret = this.currentState().user.roleCodes.find(rc => codes.find(c => c == rc)) ? true : false;
    return ret;
  }

  /**
   *
   * @param activatedRoute
   * @param actionCode
   */
  canDoAction(activatedRoute: ActivatedRoute, actionCode: string): boolean {

    if (this.isAdministrator())
      return true;

    const routeParts = (activatedRoute.snapshot.routeConfig.path || '').split('/').filter(i => !i.startsWith(':'));

    const actionRoleSection = activatedRoute.snapshot.data || {};

    const ret = actionRoleSection[actionCode] ? actionRoleSection[actionCode].find(roleCode => this.userInRole(roleCode)) != undefined : false
    return ret;
  }

  /**
   *
   */
  watchState$(): BehaviorSubject<IAuthState> {
    return this.authState;
  }

  /**
   *
   * @param userName
   * @param password
   */
  login(userName: string, password: string): Observable<ILoginResult> {
    const postData = new URLSearchParams();
    postData.set('username', userName);
    postData.set('password', password);
    postData.set('grant_type', 'password');

    const headers = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded')
      .set('deployment-type', environment.deploymentType);

    return this.httpClient.post(
      `${environment.scApiProtocol}://${environment.scApiHost}:${environment.scApiPort}/token`,
      postData.toString(),
      {
        headers: headers
      }
    ).pipe(
      map((r: ITokenResult) => {
        const user: IUser = {
          userName: r.userId || '',
          driverCode: r.scDriverCode,
          customerRole: r.scUserCustomerRole,
          customerCodes: r.scUserCustomerCodes,
          parentGroupCodes: r.scUserParentGroupCodes,
          roleCodes: (r.scUserGroups || '').split(','),
          profileImageLink: '/assets/user-32x32.png'
        };
        this.loginProcess(user, r.access_token, r.expires_in);

        console.log(`Logged in... userName: '${user.userName}', roleCodes: '${user.roleCodes.join(', ')}, customerRole: '${user.customerRole}', parentGroupCodes: '${user.parentGroupCodes}', customerCodes: '${user.customerCodes}'`);
        return {
          status: "ok",
          user: user
        };
      }),
      catchError((error: any) => {
        const message = error.error['error_description'] ? error.error['error_description']
          : error.error instanceof ErrorEvent ? `an error occurred: ${error.error.message}`
            : error.Exception ? `backend error: ${error.Exception}`
              : error.message ? error.message
              : error;

        console.log(`login error: ${message}`);
        return of({
          status: message
        });
      })
    );
  }

  /**
   *
   * @param user
   * @param token
   * @param tokenExpiration
   */
  loginProcess(user: IUser, token: string, tokenExpiration: number) {
    const authState: IAuthState = {
      isLoggedIn: true,
      user: user,
      token: token,
      tokenExpiration: tokenExpiration
    };
    this.stateSave(authState);

    console.log({ context: 'loginProcess', user: user, tokenLength: token?.length });

    this.authState.next(authState);
  }

  /**
   *
   * @param authState
   */
  private stateSave(authState: IAuthState) {
    localStorage.setItem('auth-state', JSON.stringify(authState));
  }

  /**
   *
   */
  private stateLoad(): IAuthState {
    const current = localStorage.getItem('auth-state');
    const ret: IAuthState = current ? JSON.parse(current) : undefined;
    return ret;
  }

  /**
   *
   */
  logout() {
    const authState: IAuthState = { isLoggedIn: false };
    this.stateSave(authState);

    this.authState.next(authState);
  }
}

/**
 *
 */
export interface ILoginResult {
  user?: IUser;
  status: string;
}

/**
 *
 */
export interface IAuthState {
  isLoggedIn: boolean;

  token?: string;
  tokenExpiration?: number;

  user?: IUser;
}

/**
 *
 */
export class AuthServiceRoles {
  public static readonly Administrator: string = 'Administrator';
}

/**
 *
 */
interface ITokenResult {
  access_token: string;
  token_type: string;
  expires_in: number;

  userId: string;
  scUserGroups: string;
  scDriverCode: string;
  scUserCustomerRole?: string;
  scUserCustomerCodes?: string;
  scUserParentGroupCodes?: string;
}
